import { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import api from "../api/api"; // adjust the relative path if this file isn't a sibling of api.js

// ---- Indian currency helper (matches dashboard.jsx's formatter) ----------
const formatINR = (value) =>
  `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value || 0)}`;

// FY = Apr -> Mar. Lets the admin flip between this FY and the next/prior
// without retyping a year number.
const currentFyStartYear = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

/**
 * Drawer for an admin to view/edit one user's monthly sales targets for a
 * financial year. Every month has a BOTTOM line and a TOP line; on the
 * dashboard achievement % = (achieved - bottom) / (top - bottom) * 100. Open it from a row action ("Assign Target") on your Users
 * list page, passing that row's usr_id/usr_name:
 *
 *   const [targetUser, setTargetUser] = useState(null); // { usr_id, usr_name }
 *   ...
 *   <button onClick={() => setTargetUser({ usr_id: row.usr_id, usr_name: row.usr_name })}>
 *     Assign Target
 *   </button>
 *   <AssignTargetDrawer
 *     isOpen={!!targetUser}
 *     usrId={targetUser?.usr_id}
 *     usrName={targetUser?.usr_name}
 *     onClose={() => setTargetUser(null)}
 *   />
 */
export default function AssignTargetDrawer({ isOpen, usrId, usrName, onClose }) {
  const [fyStartYear, setFyStartYear] = useState(currentFyStartYear());
  const [months, setMonths] = useState([]); // [{year, month, monthLabel, bottomLine, topLine}]
  const [applyBottom, setApplyBottom] = useState("");
  const [applyTop, setApplyTop] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!isOpen || !usrId) return;
    let cancelled = false;

    async function loadTargets() {
      setLoading(true);
      setError("");
      setSaved(false);
      try {
        const res = await api.get(`/targets/${usrId}`, { params: { fyStartYear } });
        if (!cancelled && res.data?.success) {
          setMonths(res.data.months);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || "Failed to load targets.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTargets();
    return () => {
      cancelled = true;
    };
  }, [isOpen, usrId, fyStartYear]);

  const handleLineChange = (index, field, value) => {
    setMonths((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const applyToAll = () => {
    setMonths((prev) =>
      prev.map((m) => ({
        ...m,
        bottomLine: applyBottom === "" ? m.bottomLine : applyBottom,
        topLine: applyTop === "" ? m.topLine : applyTop,
      }))
    );
  };

  const totalBottom = months.reduce((sum, m) => sum + (Number(m.bottomLine) || 0), 0);
  const totalTop = months.reduce((sum, m) => sum + (Number(m.topLine) || 0), 0);

  // A month is invalid if it has any line set but the top line isn't above
  // the bottom line (both 0 = "no target set", which is fine).
  const isMonthInvalid = (m) => {
    const bottom = Number(m.bottomLine) || 0;
    const top = Number(m.topLine) || 0;
    return (bottom > 0 || top > 0) && top <= bottom;
  };
  const hasInvalidMonth = months.some(isMonthInvalid);

  const handleSave = async () => {
    if (hasInvalidMonth) {
      setError("For every month the top line must be greater than the bottom line.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSaved(false);
    try {
      const payload = {
        fyStartYear,
        months: months.map((m) => ({
          year: m.year,
          month: m.month,
          bottomLine: Number(m.bottomLine) || 0,
          topLine: Number(m.topLine) || 0,
        })),
      };
      const res = await api.put(`/targets/${usrId}`, payload);
      if (res.data?.success) {
        setSaved(true);
      } else {
        setError(res.data?.message || "Failed to save targets.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save targets. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="hidden sm:block flex-1 bg-black/20" onClick={onClose}></div>

      <div className="w-full sm:w-[85%] md:w-[55%] max-w-full sm:max-w-xl bg-white h-full shadow-xl flex flex-col ml-auto animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Assign Target</h2>
            {usrName && <p className="text-xs text-gray-500">{usrName}</p>}
          </div>
          <button onClick={onClose}>
            <X />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-2">
              {error}
            </div>
          )}
          {saved && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded px-4 py-2">
              Targets saved.
            </div>
          )}

          {/* FY selector */}
          <div className="flex items-center gap-3 mb-5">
            <label className="text-xs text-gray-500">Financial Year</label>
            <select
              value={fyStartYear}
              onChange={(e) => setFyStartYear(Number(e.target.value))}
              className="border rounded px-3 py-1.5 text-sm bg-white"
            >
              {[currentFyStartYear() - 1, currentFyStartYear(), currentFyStartYear() + 1].map((y) => (
                <option key={y} value={y}>
                  FY{String(y).slice(-2)}-{String(y + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-10 text-gray-400">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : (
            <>
              <p className="mb-4 text-[11px] text-gray-500">
                Achievement % = (Achieved − Bottom line) ÷ (Top line − Bottom line) × 100.
                0% is the bottom line, 100% is the top line.
              </p>

              {/* Set the same lines for every month in one go */}
              <div className="mb-5 flex flex-wrap items-end gap-3 rounded border border-dashed border-gray-300 bg-gray-50 p-3">
                <div>
                  <label className="text-[11px] text-gray-500">Bottom line (all months)</label>
                  <input
                    type="number"
                    min="0"
                    value={applyBottom}
                    onChange={(e) => setApplyBottom(e.target.value)}
                    className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Top line (all months)</label>
                  <input
                    type="number"
                    min="0"
                    value={applyTop}
                    onChange={(e) => setApplyTop(e.target.value)}
                    className="w-full mt-1 border rounded px-2 py-1.5 text-sm bg-white"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyToAll}
                  disabled={applyBottom === "" && applyTop === ""}
                  className="px-3 py-1.5 text-xs text-blue-600 border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-40"
                >
                  Apply to all 12 months
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {months.map((m, i) => {
                  const invalid = isMonthInvalid(m);
                  return (
                    <div
                      key={`${m.year}-${m.month}`}
                      className={`rounded border p-3 ${invalid ? "border-red-300 bg-red-50/40" : "border-gray-200"}`}
                    >
                      <div className="text-xs font-semibold text-gray-700 mb-2">
                        {m.monthLabel} {m.year}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] text-gray-500">Bottom line</label>
                          <input
                            type="number"
                            min="0"
                            value={m.bottomLine}
                            onChange={(e) => handleLineChange(i, "bottomLine", e.target.value)}
                            className="w-full mt-1 border rounded px-2 py-1.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500">Top line</label>
                          <input
                            type="number"
                            min="0"
                            value={m.topLine}
                            onChange={(e) => handleLineChange(i, "topLine", e.target.value)}
                            className="w-full mt-1 border rounded px-2 py-1.5 text-sm"
                          />
                        </div>
                      </div>
                      {invalid && (
                        <p className="mt-1.5 text-[11px] text-red-600">
                          Top line must be greater than bottom line.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-end gap-x-6 gap-y-1 border-t pt-4 text-sm text-gray-600">
                <div>
                  FY Bottom Total: <span className="font-semibold text-gray-800">{formatINR(totalBottom)}</span>
                </div>
                <div>
                  FY Top Total: <span className="font-semibold text-gray-800">{formatINR(totalTop)}</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 sm:px-6 py-4 flex flex-wrap justify-end gap-3">
          <button onClick={onClose} disabled={submitting} className="px-5 py-2 border rounded disabled:opacity-50">
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || loading || hasInvalidMonth}
            className="px-5 py-2 bg-blue-600 text-white rounded flex items-center gap-2 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? "Saving..." : "Save Targets"}
          </button>
        </div>
      </div>
    </div>
  );
}